import React, { useRef, useEffect, useState, useCallback, memo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Send, AutoAwesome, Visibility, ContentCopy, Refresh as RefreshIcon } from '@mui/icons-material';
import { ChatPanelProps } from '../../../types';
import MarkupMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/MarkupMAIQ';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from '../../../redux/headerSlice/selectors';
import { logger } from 'app/utils/logger';
import { EmailTemplatePreviewDialog } from '../../EmailTemplatePreviewDialog';

// Main container with full height
const ChatContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  flexDirection: 'column',
  height: '100%', // Full height of parent container
  width: '100%',
  overflow: 'hidden',
  padding: theme.spacing(0),
  minHeight: '100vh', // Ensure minimum viewport height
}));

// Messages area - takes most of the space
const MessagesArea = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: theme.spacing(3, 4),
  display: 'flex',
  flexDirection: 'column',
  // gap: theme.spacing(4),
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  // Custom scrollbar styles for theme compatibility
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[500],
    },
  },
  '&::-webkit-scrollbar-corner': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
  },
}));

// Message container with left-right layout
const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  display: 'flex',
  justifyContent: isUser ? 'flex-end' : 'flex-start',
  width: '100%',
  marginBottom: theme.spacing(2),
  alignItems: 'flex-start',
}));

// Message bubble
const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  maxWidth: '70%',
  padding: theme.spacing(2, 3),
  borderRadius: '12px',
  backgroundColor: isUser 
    ? theme.palette.primary.main 
    : theme.palette.mode === 'dark' 
      ? theme.palette.grey[800] 
      : theme.palette.grey[100],
  color: isUser ? theme.palette.primary.contrastText : theme.palette.text.primary,
  fontSize: theme.typography.body1.fontSize, // 0.875rem from theme
  lineHeight: theme.typography.body1.lineHeight, // 1.6 from theme
  fontFamily: theme.typography.fontFamily,
  wordWrap: 'break-word',
  boxShadow: 'none',
  border: 'none',
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  margin: theme.spacing(2, 0),
}));

// Input area at bottom
const InputArea = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  padding: theme.spacing(2, 4),
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  gap: theme.spacing(1),
  width: '100%',
  boxSizing: 'border-box',
}));

// Processing message container
const ProcessingMessageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5), // Very small gap between icon and text
  marginBottom: theme.spacing(2), // Minimal spacing between messages
  paddingLeft: 0, // No left padding
}));

// Processing message text with skeleton loading effect
const ProcessingMessageText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body1.fontSize, // 0.875rem from theme (larger for better visibility)
  fontStyle: 'italic',
  color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : theme.palette.success.main, // Green for bright mode
  fontWeight: 500, // Slightly bolder for better visibility
  lineHeight: 1, // 1.6 from theme
  fontFamily: theme.typography.fontFamily,
  background: `linear-gradient(90deg, ${theme.palette.mode === 'dark' ? theme.palette.text.secondary : theme.palette.success.main} 25%, ${theme.palette.grey[300]} 50%, ${theme.palette.mode === 'dark' ? theme.palette.text.secondary : theme.palette.success.main} 75%)`,
  backgroundSize: '200% 100%',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  animation: 'skeletonLoading 2s ease-in-out infinite',
  '@keyframes skeletonLoading': {
    '0%': {
      backgroundPosition: '200% 0',
    },
    '100%': {
      backgroundPosition: '-200% 0',
    },
  },
}));

// Star icon for processing messages with zoom animation
const ProcessingIcon = styled(AutoAwesome)(({ theme }) => ({
  fontSize: '16px',
  color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : theme.palette.success.main, // Green for bright mode
  animation: 'zoomInOut 2s ease-in-out infinite',
  '@keyframes zoomInOut': {
    '0%': {
      transform: 'scale(1)',
    },
    '50%': {
      transform: 'scale(1.1)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },
}));

// Copy icon styled component
const CopyIcon = styled(IconButton)(({ theme }) => ({
  width: '20px',
  height: '20px',
  minWidth: '20px',
  minHeight: '20px',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
  borderRadius: '50%',
  boxShadow: theme.shadows[1],
  opacity: 0,
  transition: 'all 0.2s ease-in-out',
  position: 'absolute',
  top: '8px',
  right: '8px',
  zIndex: 1,
  border: '1px solid',
  borderColor: theme.palette.divider,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[2],
  },
}));

// Message bubble container with hover effect for copy icon
const MessageBubbleContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  '&:hover .copy-icon': {
    opacity: 1,
  },
}));

// Preview icon styled component
const PreviewIconButton = styled(IconButton)(({ theme }) => ({
  width: '20px',
  height: '20px',
  minWidth: '20px',
  minHeight: '20px',
  padding: '4px',
  marginLeft: '4px',
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.primary.main,
  },
}));

// Suggestion button container
const SuggestionButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}));


const CleanChatPanel: React.FC<ChatPanelProps> = memo(({
  chatHistory,
  onOptionSelect,
  onSendMessage,
  inputText,
  setInputText,
  loading = false,
  onReset,
  suggestions = [],
  isInputRestricted = false,
  selectedFilters = [],
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);
  
  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplateId, setPreviewTemplateId] = useState<string | undefined>(undefined);
  
  // Get selected dealer
  const selectedDealer = useSelector(selectDealerSelected);
  const currentDealerId = selectedDealer?.dealer || '';

  // Auto scroll to bottom - memoized
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, loading, scrollToBottom]);

  const handleSendMessage = useCallback(() => {
    if (inputText.trim() && !loading && !isInputRestricted) {
      onSendMessage(inputText.trim());
    }
  }, [inputText, loading, isInputRestricted, onSendMessage]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isInputRestricted) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [isInputRestricted, handleSendMessage]);

  const handleCopyMessage = useCallback((text: string, messageIndex: number) => {
    // For MarkupMAIQ content, we need to extract plain text
    const plainText = text.replace(/<[^>]*>/g, '').replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
    navigator.clipboard.writeText(plainText).then(() => {
      setCopiedMessageId(messageIndex);
      setTimeout(() => {
        setCopiedMessageId(null);
      }, 3000);
    }).catch((err) => {
      // Silent fail
    });
  }, []);

  // Handle preview icon click
  const handlePreviewClick = useCallback((e: React.MouseEvent, templateId: string) => {
    e.stopPropagation(); // Prevent triggering the button click
    
    if (!currentDealerId) {
      logger.warn('Dealer ID is required for preview');
      return;
    }

    logger.info('Opening email template preview', { dealerId: currentDealerId, templateId });
    setPreviewTemplateId(templateId);
    setPreviewOpen(true);
  }, [currentDealerId]);

  // Close preview modal
  const handleClosePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewTemplateId(undefined);
  }, []);

  // Use selectedFilters prop directly

  return (
    <ChatContainer>
      <MessagesArea>
        {chatHistory
          .filter(msg => msg.role === 'user' || msg.role === 'assistant')
          .map((msg, index) => (
            <MessageContainer key={msg.id || index} isUser={msg.role === 'user'}>
              {msg.role === 'user' ? (
                // User message - right aligned
                <MessageBubble isUser={true}>
                  {msg.content}
                </MessageBubble>
              ) : (
                // AI message - left aligned with unified container for message and suggestions
                <Box >
                  {/* Unified container for AI message and suggestions */}
                  <MessageBubbleContainer>
                    <Box sx={{
                      backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : 'grey.100',
                      borderRadius: '12px',
                      padding: '16px',
                      border: '1px solid',
                      borderColor: 'divider',
                      // boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      {/* AI Message Text */}
                      <Box>
                        <MarkupMAIQ content={msg.content} />
                      </Box>
                      
                      {/* Show suggestions for the last assistant message */}
                      {index === chatHistory.filter(m => m.role === 'user' || m.role === 'assistant').length - 1 && suggestions.length > 0 && (
                        <Box sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '8px'
                        }}>
                          {suggestions.map((suggestion) => (
                            <SuggestionButtonContainer key={suggestion.id}>
                              <Button
                                onClick={() => onOptionSelect(suggestion)}
                                disabled={loading || isInputRestricted}
                                sx={{
                                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : '#ffffff',
                                  color: 'text.primary',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: '20px',
                                  padding: '2px 16px',
                                  textTransform: 'none',
                                  fontSize: '0.875rem',
                                  fontWeight: 400,
                                  boxShadow: 'none',
                                  '&:hover': {
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'action.hover' : '#f5f5f5',
                                    borderColor: 'grey.300',
                                  },
                                  '&:disabled': {
                                    backgroundColor: 'action.disabledBackground',
                                    color: 'action.disabled',
                                    borderColor: 'action.disabled',
                                  }
                                }}
                              >
                                {suggestion.text}
                              </Button>
                              {suggestion.entity_type === 'EMAIL_TEMPLATE' && (
                                <Tooltip title="Preview Email Template" arrow>
                                  <PreviewIconButton
                                    size="small"
                                    onClick={(e) => handlePreviewClick(e, suggestion.id)}
                                    disabled={loading || isInputRestricted}
                                    aria-label="Preview email template"
                                  >
                                    <Visibility sx={{ fontSize: 16 }} />
                                  </PreviewIconButton>
                                </Tooltip>
                              )}
                            </SuggestionButtonContainer>
                          ))}
                        </Box>
                      )}
                    </Box>
                    
                    {/* Copy Icon positioned at top right of message bubble */}
                    <CopyIcon
                      className="copy-icon"
                      onClick={() => handleCopyMessage(msg.content, index)}
                      size="small"
                      aria-label="Copy message"
                      sx={{
                        color: 'text.primary',
                      }}
                    >
                      {copiedMessageId === index ? (
                        <Box sx={{ color: 'success.main', fontSize: 12 }}>✓</Box>
                      ) : (
                        <ContentCopy sx={{ fontSize: 12, color: 'inherit' }} />
                      )}
                    </CopyIcon>
                  </MessageBubbleContainer>
                </Box>
              )}
            </MessageContainer>
          ))}

        {/* Dynamic processing messages from socket/system */}
        {chatHistory
          .filter(msg => msg.processingType && msg.role === 'system')
          .map((msg, index) => (
            <ProcessingMessageContainer key={`processing-${index}`}>
              <ProcessingIcon />
              <ProcessingMessageText>
                {msg.content}
              </ProcessingMessageText>
            </ProcessingMessageContainer>
          ))
        }

        {/* Mia is thinking message bubble */}
        {chatHistory.some(msg => msg.processingType && msg.role === 'system') && (
          <MessageContainer isUser={false}>
            <Box sx={{ width: '100%' }}>
              <MessageBubble isUser={false}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={20} sx={{ color: 'text.secondary' }} />
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: (theme) => theme.palette.mode === 'dark' ? 'text.secondary' : 'text.primary',
                      fontWeight: 500,
                      fontSize: '0.875rem'
                    }}
                  >
                    Mia is thinking...
                  </Typography>
                </Box>
              </MessageBubble>
            </Box>
          </MessageContainer>
        )}

        <div ref={messagesEndRef} />
      </MessagesArea>

      <InputArea>
        {/* Chat Input Container */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          padding: '8px',
          width: '100%',
          minHeight: '48px',
          boxSizing: 'border-box',
        }}>
          {/* Text Input Area */}
          <TextField
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isInputRestricted ? "Please select from the suggestions above..." : "Reply to Mia"}
            disabled={loading || isInputRestricted}
            multiline
            maxRows={3}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: '0.875rem',
                fontFamily: 'inherit',
                color: 'text.primary',
                '& input::placeholder': {
                  color: 'text.secondary',
                  opacity: 1
                }
              }
            }}
            sx={{
              flex: 1,
              '& .MuiInput-root': {
                padding: '8px 12px',
                fontSize: '0.875rem'
              }
            }}
          />
          
          {/* Circular Send Button */}
          <IconButton
            onClick={handleSendMessage}
            disabled={!inputText.trim() || loading || isInputRestricted}
            sx={{
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              padding: 0,
              marginLeft: '8px',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
              '&:disabled': {
                backgroundColor: 'action.disabledBackground',
                borderColor: 'action.disabled',
                color: 'action.disabled'
              }
            }}
          >
            <Send sx={{ fontSize: 16, color: 'text.primary' }} />
          </IconButton>

          {/* Reset Button */}
          {onReset && (
            <Tooltip title="Reset Chat" arrow>
              <IconButton
                onClick={onReset}
                sx={{
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  padding: 0,
                  marginLeft: '8px',
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderColor: 'error.main',
                    color: 'error.main'
                  }
                }}
              >
                <RefreshIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

      </InputArea>

      {/* Preview Modal */}
      <EmailTemplatePreviewDialog
        open={previewOpen}
        onClose={handleClosePreview}
        templateId={previewTemplateId}
        dealerId={currentDealerId}
      />
    </ChatContainer>
  );
});

// Add display name for debugging
CleanChatPanel.displayName = 'CleanChatPanel';

export default CleanChatPanel;
